import { ForteReimbursementStats } from '@Shared/interfaces/Forte/ForteInterfaces';
import { StripeAccount, StripePerson } from '@Shared/interfaces/Stripe';
import { ICompanyModel, IUserModel } from '@Shared/models';

import { Api as apiService } from '../api';

export abstract class CompanyApi {
  static update(modifiedCompany: Partial<ICompanyModel>): Promise<ICompanyModel> {
    return apiService.put('/api/web/org', modifiedCompany).then((request) => request.json());
  }

  static get(includes: (keyof ICompanyModel)[]) {
    return apiService.get('/api/web/org', includes).then((request) => request.json() as any);
  }

  static sendWelcomeCompanyData(data: Partial<ICompanyModel>) {
    return apiService.post(`welcomeCompany?company_id=${data.id}`, data).then((request) => request.json() as any);
  }

  static sendAdditionalCompanyData(data: Partial<ICompanyModel>) {
    return apiService.post(`signup/additional_company`, data).then((request) => request.json() as any);
  }

  static signupForEmburseCards(company: ICompanyModel, user: IUserModel) {
    return apiService
      .post(`/onboardUser/emburse-card-signup`, {
        company,
        user
      })
      .then((request) => request.json() as any);
  }
  static getCompanyGatewayConnections(companyId: string) {
    return apiService.get(`/onboardUser/${companyId}/connections`).then((request) => request.json() as any);
  }
  static activateCompany(companyId: string) {
    return apiService.patch(`/onboardUser/${companyId}/activate-company`).then((request) => request.json() as any);
  }

  static hasCards(companyId: string) {
    return apiService.get(`/api/web/accounts/company/${companyId}/hasCards`).then((request) => request.json() as any);
  }

  static getFundingSource(companyId: string) {
    return apiService.get(`/api/web/accounts/company/${companyId}/fundingSources`).then((request) => request.json() as any);
  }

  static getIncompleteInvoicesAmount(): Promise<{ amountDue: number }> {
    return apiService.get(`/api/web/org/incompleteInvoicesAmount`).then((request) => request.json() as any);
  }

  static upgradeBillingPlan(companyId: string): Promise<ICompanyModel> {
    return apiService.responseToJSONAndToastError(apiService.patch(`/api/web/accounts/company/${companyId}/upgradeBillingPlan`));
  }

  static updateCompanyBillingPlan(billing_plan: ICompanyModel['billing_plan'], options?: any): Promise<ICompanyModel> {
    return apiService.responseToJSON(
      apiService.put(`/api/web/accounts/company/updateCompanyBillingPlan`, { billing_plan: billing_plan, ...options })
    );
  }

  static upgradeCompanyPlanSize(plan_size: number): Promise<ICompanyModel> {
    return apiService.responseToJSONAndToastError(apiService.patch(`/api/web/accounts/company/upgradeCompanyPlanSize`, { plan_size }));
  }

  static upgradeMonthlyToYearlyBillingPlan(plan_size: number): Promise<ICompanyModel> {
    return apiService.responseToJSONAndToastError(
      apiService.patch(`/api/web/accounts/company/upgradeMonthlyToYearlyBillingPlan`, { plan_size })
    );
  }

  static scheduleDowngradeYearlyToMonthlyBillingPlan(): Promise<ICompanyModel> {
    return apiService.responseToJSONAndToastError(apiService.put(`/api/web/org/scheduleAnnualPlanDowngrade`));
  }

  static updateImplementationOptions(implementation_options: ICompanyModel['implementation_options']): Promise<ICompanyModel> {
    return apiService.responseToJSON(
      apiService.put(`/api/web/org/updateImplementationOption`, { implementation_options: implementation_options })
    );
  }

  static activateGuidedImplementation(): Promise<ICompanyModel> {
    return apiService.responseToJSONAndToastError(apiService.put(`/api/web/org/activateGuidedImplementation`));
  }

  static requestTaxExemption(): Promise<ICompanyModel> {
    return apiService.responseToJSON(apiService.post(`/api/web/org/taxExemptionRequest`));
  }

  static updateOrgBlockReimbursableExpensesFlag() {
    return apiService.responseToJSON(apiService.put(`/api/web/org/updateOrgBlockReimbursableExpensesFlag`));
  }

  static updateTripsEnabledFlag(): Promise<{ enabled: boolean }> {
    return apiService.responseToJSON(apiService.put(`/api/web/org/updateTripsEnabledFlag`));
  }

  static companyHasApprovedExpense(): Promise<boolean> {
    return apiService.responseToJSON(apiService.get(`/api/web/org/companyHasApprovedExpense`));
  }

  static retrieveStripeCompany(): Promise<StripeAccount> {
    return apiService.responseToJSON(apiService.get(`/api/web/org/stripe`));
  }

  static retrieveStripeOwners(): Promise<StripePerson[]> {
    return apiService.responseToJSON(apiService.get(`/api/web/org/stripe/owners`));
  }

  static retrieveStripeUsage(): Promise<{ usageInStripe: number }> {
    return apiService.responseToJSON(apiService.get(`/api/web/org/stripe/usage`));
  }

  static retrieveForteReimbursementStats(): Promise<ForteReimbursementStats> {
    return apiService.responseToJSON(apiService.get(`/api/web/org/forte/reimbursementStats`));
  }

  static retrieveForteApplication(): Promise<any> {
    return apiService.responseToJSON(apiService.get(`/api/web/org/forte/application`));
  }

  static submitForteApplication(body: any): Promise<ICompanyModel> {
    return apiService.responseToJSONAndToastError(apiService.post(`/api/web/org/forte/application`, body));
  }

  static getMigrationInfo(): Promise<any> {
    return apiService.responseToJSON(apiService.get(`/api/web/org/migration`));
  }

  static updateMigration(body: any): Promise<any> {
    return apiService.responseToJSON(apiService.post(`/api/web/org/migration`, body));
  }

  static finalizeMigration(): Promise<any> {
    return apiService.responseToJSON(apiService.post(`/api/web/org/finalizeMigration`));
  }

  static cancelAccount(companyId: string, data: any) {
    return apiService.post(`/account/cancel?company_id=${companyId}`, data);
  }

  static closeAccount(companyId: string, data: any) {
    return apiService.post(`/account/close?company_id=${companyId}`, data).then((request) => request.json() as any);
  }
}
